/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte: nothing in it is
 * design content, and both consoles make the same decision about an unauthenticated arrival for the
 * same reason. `docs/adr/0021-two-consoles-and-no-shared-shell.md` records why `src/auth/` is
 * duplicated rather than shared, and `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md`
 * records the decision this module takes.
 */

import { useEffect, useRef, type ReactNode } from "react";

import { StateView } from "../ui/StateView.tsx";
import type { ConsoleAuthority } from "./authority.ts";

/**
 * What happens to somebody who arrives at the console not signed in.
 *
 * The operator's decision, taken after signing in to the deployed console and recorded in
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md`: **Keycloak's page is the sign-in page.**
 * An unauthenticated arrival goes straight to the authorization endpoint instead of being shown a
 * console form whose only field the provider's own form asks for again. ADR 0018's flow is unchanged
 * — same authorization code, same PKCE, same token handling. Only the question of what the console
 * draws before starting it is answered differently.
 *
 * This is one function and one component rather than an effect in the switch above, for two reasons.
 *
 * **A redirect on every unauthenticated render is a loop waiting for a reason.** Two arrivals are
 * unauthenticated and must still be shown a page, and neither involves anybody failing to sign in:
 * the operator who has just signed out, and the tab that has already been sent to the provider and
 * come back with nothing. Deciding that in a named, total function means the guard can be read and
 * tested, rather than inferred from the order of some effects.
 *
 * **The authority stays a statement about the session.** It still asks the provider for nothing when
 * it starts; it publishes the facts, and the decision is taken here, above it.
 */

/** The two facts a `signed_out` state carries, which are all this decision needs. */
export interface UnauthenticatedArrival {
  readonly afterSignOut: boolean;
  readonly afterAbandonedRequest: boolean;
}

/**
 * Where an unauthenticated arrival goes.
 *
 * `because` is not read by the console — the switch only needs to know which of the two to render —
 * and it exists so a test can assert *which* guard held rather than that some guard held. A guard
 * that fires for the wrong reason is a guard that will stop firing when the other reason changes.
 */
export type Arrival =
  | { readonly kind: "to_the_provider" }
  | { readonly kind: "to_a_page"; readonly because: "after_sign_out" | "request_unanswered" };

export function arrivalFor(facts: UnauthenticatedArrival): Arrival {
  /*
   * Signing out has to be something an operator can finish.
   *
   * The sign-out flow ends back on this origin with no session, which is an unauthenticated arrival
   * like any other — so redirecting it would take the operator straight to the provider's form they
   * had just left, and "sign out" would mean "sign in again". The design draws the page this lands
   * on, with its green confirmation banner, and it is the page the operator asked for.
   */
  if (facts.afterSignOut) return { kind: "to_a_page", because: "after_sign_out" };

  /*
   * The loop guard.
   *
   * An authorization request still in storage means this tab has already left for the provider and
   * returned without a code and without an error: Back was pressed, or the provider came back to the
   * redirect URI without answering. Leaving again would return the same way, and the console would
   * bounce between itself and the provider for as long as the browser allowed. The authority keeps
   * the request where it is, so this holds across a reload too, and only a real callback or the
   * operator pressing Continue clears it.
   */
  if (facts.afterAbandonedRequest) return { kind: "to_a_page", because: "request_unanswered" };

  return { kind: "to_the_provider" };
}

/**
 * The frame between arriving and leaving.
 *
 * `beginSignIn` discovers the provider's metadata before it can build an authorization URL, which is
 * a network round trip, so there is something on screen here rather than nothing: the design's
 * loading state, centred, which is what this console already draws while it is starting — because
 * from the operator's point of view it still is. The instant discovery answers, the authority
 * publishes `authenticating` and the waiting page takes over; if discovery fails it publishes
 * `failed` and the failure page does. Neither of those comes back here.
 *
 * Asked once. The ref is not defensive dressing: React's `StrictMode` runs an effect, cleans it up
 * and runs it again on mount, and two authorization requests would leave two verifiers in storage of
 * which only the second could be redeemed.
 */
export function LeavingForTheProvider({
  authority,
}: {
  readonly authority: ConsoleAuthority;
}): ReactNode {
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    void authority.beginSignIn();
  }, [authority]);

  return (
    <div className="boot">
      <StateView state={{ kind: "loading" }} purpose="The console is sending you to sign in." />
    </div>
  );
}
